import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { 
  Calendar, MapPin, User, ArrowLeft, Grid, List, 
  Camera, Clock, DollarSign, Shield, Eye, Map, X,
  ChevronDown, ChevronRight, FileText
} from 'lucide-react';
import { db, auth } from '../firebase';
import { doc, getDoc, getDocs, collection, query, where, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { formatDateRange } from '../utils/dates';
import logo from '../assets/curaffect-logo.png';
import icon from '../assets/curaffect-icon.png';

const ExhibitView = () => {
  const {exhibitId } = useParams();
  const [exhibit, setExhibit] = useState(null);
  const [showDescription, setShowDescription] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filterGroup, setFilterGroup] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedArea = searchParams.get('case');
  const [expandedAreas, setExpandedAreas] = useState(new Set());
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check authentication status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        
        // Check if user is admin
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsAdmin(userData?.role === 'admin');
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (exhibitId) {
      setLoading(true);
      setExhibit(null);
      setNotFound(false);
      loadExhibit();
    }
  }, [exhibitId]);

  const loadExhibit = async () => {
    try {
      // Load exhibit data
      // Find the exhibit by slug, falling back to its document id
      let exhibitDoc = null;

      const bySlug = await getDocs(
        query(collection(db, 'exhibits'), where('slug', '==', exhibitId), limit(1))
      );

      if (!bySlug.empty) {
        exhibitDoc = bySlug.docs[0];
      } else {
        const byId = await getDoc(doc(db, 'exhibits', exhibitId));
        if (byId.exists()) exhibitDoc = byId;
      }

      if (!exhibitDoc) {
        setNotFound(true);
        return;
      }

      const exhibitData = { id: exhibitDoc.id, ...exhibitDoc.data() };

      setExhibit(exhibitData);

      // Load artifacts with all requests in flight at once, rather than
      // waiting for each round trip in turn. allSettled keeps one bad
      // artifact (deleted, or blocked by rules) from killing the batch.
      if (exhibitData.artifactIds && exhibitData.artifactIds.length > 0) {
        const results = await Promise.allSettled(
          exhibitData.artifactIds.map(id => getDoc(doc(db, 'artifacts', id)))
        );

        results.forEach((r, i) => {
          const id = exhibitData.artifactIds[i];
          if (r.status === 'rejected') {
            console.warn('Artifact unreadable (permissions?):', id, r.reason?.code);
          } else if (!r.value.exists()) {
            console.warn('Artifact not found:', id);
          }
        });

        setArtifacts(
          results
            .filter(r => r.status === 'fulfilled' && r.value.exists())
            .map(r => ({ id: r.value.id, ...r.value.data() }))
        );
      }
    } catch (error) {
      console.error('Error loading exhibit:', error);
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Exhibit Not Found</h2>
          <p className="text-gray-600 mb-4">The exhibit you're looking for doesn't exist.</p>
          <Link to="/?tab=exhibits" className="text-blue-600 hover:text-blue-800 flex items-center gap-2 justify-center">
            <ArrowLeft className="w-4 h-4" />
            Back to Exhibits
          </Link>
        </div>
      </div>
    );
  }

  if (!exhibit) {
    return null;
  }

  // Check if exhibit is unpublished and user is not admin
  if (!exhibit.published && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Show exhibit image if available */}
            {exhibit.headerImage && (
              <div className="relative h-64 sm:h-80 md:h-[28rem] bg-gray-900 overflow-hidden">
                <img 
                  src={exhibit.headerImage} 
                  alt={exhibit.name}
                  className="w-full h-full object-cover sm:object-cover object-contain opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              </div>
            )}
            
            <div className="p-6 text-center">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{exhibit.name}</h2>
              <p className="text-gray-600 mb-4">This exhibit is currently being prepared.</p>
              
              {/* Show date range if available */}
              {(exhibit.startDate || exhibit.endDate) && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{formatDateRange(exhibit.startDate, exhibit.endDate, true)}</span>
                  </div>
                </div>
              )}
              
              <Link to="/?tab=exhibits" className="text-blue-600 hover:text-blue-800 flex items-center gap-2 justify-center">
                <ArrowLeft className="w-4 h-4" />
                Back to Exhibits
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show admin preview banner if exhibit is unpublished and user is admin
  const showAdminPreview = !exhibit.published && isAdmin;

  // Display groups actually present in this exhibit
  const displayGroups = [...new Set(
    artifacts.map(a => a.displayGroup).filter(Boolean)
  )].sort();

  // Artifacts remaining after the display group filter
  const visibleArtifacts = filterGroup === 'all'
    ? artifacts
    : artifacts.filter(a => a.displayGroup === filterGroup);

  // Bucket the visible artifacts by area/case
  const artifactsByArea = visibleArtifacts.reduce((acc, a) => {
    const area = a.location || 'Unassigned';
    (acc[area] = acc[area] || []).push(a);
    return acc;
  }, {});

  // Area headings, naturally sorted, with Unassigned pushed to the end
  const order = exhibit.areaOrder || [];
  const areaNames = Object.keys(artifactsByArea).sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b, undefined, { numeric: true });
  });

    console.log('loaded:', artifacts.length, 'visible:', visibleArtifacts.length);
  console.log('locations:', artifacts.map(a => `${a.name}|${a.location}`));
  console.log('areas:', areaNames);

  const toggleArea = (area) => {
    setExpandedAreas(prev => {
      const next = new Set(prev);
      if (next.has(area)) {
        next.delete(area);
      } else {
        next.add(area);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Preview Banner */}
      {showAdminPreview && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-2 text-sm text-yellow-800">
              <Eye className="w-4 h-4" />
              <span className="font-medium">Admin Preview:</span>
              <span>This exhibit is unpublished and only visible to administrators.</span>
            </div>
          </div>
        </div>
      )}

      {/* Header Image - Taller banner style */}
      {exhibit.headerImage && (
        <div className="relative h-56 md:h-72 bg-gray-900 overflow-hidden">
          <img 
            src={exhibit.headerImage} 
            alt={exhibit.name}
            className="w-full h-full object-cover opacity-90"
          />
          <Link 
            to="/?tab=exhibits" 
            className="absolute top-8 left-4 bg-white/90 rounded-lg px-4 py-3 hover:bg-white transition-colors"
          >
            <img src={icon} alt="Curaffect" className="h-16 md:h-20 w-auto" />
          </Link>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Back Button */}
        <Link 
          to="/?tab=exhibits" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Exhibits
        </Link>
        
        {/* Exhibit Info */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {!exhibit.headerImage && (
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{exhibit.name}</h1>
          )}

          {exhibit.overview && (
            <p className="text-gray-700 text-lg mb-4">{exhibit.overview}</p>
          )}
          
          <div className="flex flex-wrap justify-between gap-x-8 gap-y-4 text-sm">
            {(exhibit.startDate || exhibit.endDate) && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Exhibition Dates</p>
                  <p className="text-gray-600">
                    {formatDateRange(exhibit.startDate, exhibit.endDate)}
                  </p>
                </div>
              </div>
            )}
            
            {exhibit.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Location</p>
                  <p className="text-gray-600">{exhibit.location}</p>
                </div>
              </div>
            )}
            
            {exhibit.curator && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Curated by</p>
                  <p className="text-gray-600">{exhibit.curator}</p>
                </div>
              </div>
            )}
            
            {exhibit.galleryLayoutImage && (
              <div className="flex items-start gap-3">
                <Map className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Gallery Layout</p>
                  <a 
                    href={exhibit.galleryLayoutImage} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    View floor plan
                  </a>
                </div>
              </div>
            )}

        {exhibit.description && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">About this Exhibit</p>
                  <button
                    onClick={() => setShowDescription(true)}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Read more
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Exhibit Picture */}
        {!selectedArea && exhibit.exhibitPicture && (
          <div className="mb-6">
            <img 
              src={exhibit.exhibitPicture} 
              alt={exhibit.name}
              className="w-full rounded-lg shadow-sm"
            />
          </div>
        )}
        
        {/* Artifacts Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Artifacts in this Exhibit ({artifacts.length})
            </h2>
            <div className="flex items-center gap-3">
              {displayGroups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All display groups</option>
                  {displayGroups.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              )}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          {artifacts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Grid className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No artifacts have been added to this exhibit yet.</p>
            </div>
          ) : viewMode === 'grid' ? (
            selectedArea === null ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {areaNames.map(area => {
                  const details = (exhibit.areaDetails || {})[area] || {};
                  return (
                    <div
                      key={area}
                      onClick={() => setSearchParams({ case: area })}
                      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                    >
                      {details.image ? (
                        <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                          <img src={details.image} alt={area} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                          <Camera className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">{area}</h3>
                        <p className="text-sm text-gray-500 mb-2">
                          {artifactsByArea[area].length} artifact{artifactsByArea[area].length === 1 ? '' : 's'}
                        </p>
                        {details.description && (
                          <p className="text-sm text-gray-600 line-clamp-3">{details.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                <button
                  onClick={() => setSearchParams({})}
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  All cases
                </button>

                {(() => {
                  const details = (exhibit.areaDetails || {})[selectedArea] || {};
                  return (
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{selectedArea}</h3>
                      {details.image && (
                        <img src={details.image} alt={selectedArea} className="w-full rounded-lg mb-4" />
                      )}
                      {details.audio && (
                        <div className="w-full mb-4">
                          <audio controls src={details.audio} style={{ width: '100%', height: '60px' }} />
                        </div>
                      )}
                      {details.description && (
                        <p className="text-gray-700 whitespace-pre-line">{details.description}</p>
                      )}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(artifactsByArea[selectedArea] || []).map((artifact) => (
                    <div
                      key={artifact.id}
                      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedArtifact(artifact)}
                    >
                      {artifact.images && artifact.images.length > 0 ? (
                        <div className="aspect-[3/4] bg-gray-100 rounded-t-lg overflow-hidden">
                          <img src={artifact.images[0]} alt={artifact.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="aspect-[3/4] bg-gray-100 rounded-t-lg flex items-center justify-center">
                          <Camera className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">{artifact.name}</h3>
                        {artifact.manufacturer && (
                          <p className="text-sm text-gray-600 mb-1">{artifact.manufacturer}</p>
                        )}
                        {artifact.year && <p className="text-sm text-gray-500">{artifact.year}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Artifact</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Area/Case</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Year</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Display Group</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {visibleArtifacts.map((artifact) => (
                    <tr 
                      key={artifact.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedArtifact(artifact)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {artifact.images && artifact.images.length > 0 ? (
                            <img 
                              src={artifact.images[0]} 
                              alt={artifact.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <Camera className="w-5 h-5 text-gray-300" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{artifact.name}</p>
                            {artifact.manufacturer && (
                              <p className="text-sm text-gray-600">{artifact.manufacturer}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{artifact.location || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{artifact.year || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{artifact.displayGroup || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Artifact Detail Modal */}
      {selectedArtifact && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedArtifact(null)}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setSelectedArtifact(null)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg z-10 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Image Gallery */}
              {selectedArtifact.images && selectedArtifact.images.length > 0 && (
                <div className="aspect-[16/9] bg-gray-100 overflow-hidden">
                  <img 
                    src={selectedArtifact.images[0]} 
                    alt={selectedArtifact.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedArtifact.name}
                </h2>
                
                {selectedArtifact.manufacturer && (
                  <p className="text-lg text-gray-600 mb-4">
                    by {selectedArtifact.manufacturer}
                  </p>
                )}
                
                {selectedArtifact.description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700">{selectedArtifact.description}</p>
                  </div>
                )}
                
                {selectedArtifact.historicalContext && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Historical Context</h3>
                    <p className="text-gray-700">{selectedArtifact.historicalContext}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedArtifact.location && (
                    <div>
                      <p className="text-gray-500">Location</p>
                      <p className="font-medium">{selectedArtifact.location}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDescription && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowDescription(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{exhibit.name}</h2>
                <button
                  onClick={() => setShowDescription(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-gray-700 whitespace-pre-line">{exhibit.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExhibitView;
